import classNames from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { toggleViewerInsightsUI } from 'actions/viewerInsights';
import { SHARE_CTA_INSTALL_SLACK_CLICKED } from 'constants/events';
import { SLACK_APP } from 'constants/oAuthKeys';
import { SHOW_SLACK_APP_UPSELL } from 'constants/triggers';
import { completeTriggerCreator } from 'creators/triggers';
import * as analytics from 'utilities/analytics';
import * as auth from 'utilities/auth';
import { requestAnimationFrame } from 'utilities/dom';
import * as extension from 'utilities/extension';
import { getParam } from 'utilities/url';
import videoSession from 'utilities/video-session';

import { selectIsActive as attachmentsActive } from 'selectors/attachments';
import { selectEditorIsActive } from 'selectors/editor';
import { selectVideo, selectVideoUseEmojis } from 'selectors/shareVideo';
import { selectHasPro } from 'selectors/user';
import { selectIsActive as viewerInsightsActive } from 'selectors/viewerInsights';

import MoveToTeam from './move-to-team';
import ShareLoomTeamCTA from './share-loom-team-cta';
import {
  ROLLOUT_FILE_ATTACHMENTS,
  ROLLOUT_VIDEO_ANALYTICS_SESSION,
} from '../../../../../shared/constants/featureFlag';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import AttachmentWidget from 'components/share-video/attachment-widget';
import EditorWidget from 'components/share-video/editor-widget';
import ShareVideoWidget from 'components/share-video/share-video-widget';
import ViewerInsights from 'components/share-video/viewer-insights';

import './styles.less';

class RightPanel extends Component {
  defaultStyle = {
    width: 'auto',
    height: 'auto',
    overflow: 'auto',
  };

  connectSlack = () => {
    const showSlackUpsell = this.props.completeTriggerCreator.bind(
      this,
      SHOW_SLACK_APP_UPSELL
    );

    this.connect(showSlackUpsell);
  };

  render() {
    const {
      hasPro,
      speechFlow,
      theaterMode,
      useEmojis,
      userCanEdit,
      video,
      viewerInsightsActive,
    } = this.props;

    const hasEditorPro = userCanEdit && hasPro;

    const shareVideoWidget = (
      <ShareVideoWidget
        key="share-widget"
        className="share-methods"
        showUpgradeModal={this.props.showUpgradeModal}
        video={video}
      />
    );

    return (
      <div
        className={classNames('right-panel', {
          'speech-flow': speechFlow,
          'theater-mode': theaterMode,
          'use-emojis': useEmojis,
        })}
      >
        {shareVideoWidget}
        {hasEditorPro && (
          <FeatureFlaggedComponent
            flag={ROLLOUT_VIDEO_ANALYTICS_SESSION}
            activationValue={true}
          >
            <ViewerInsights
              activated={viewerInsightsActive}
              onActivate={this.props.toggleViewerInsightsUI}
            />
          </FeatureFlaggedComponent>
        )}
        <ShareLoomTeamCTA />
        <MoveToTeam />
        <EditorWidget
          showUpgradeModal={this.props.showUpgradeModal}
          onEnterTrimMode={this.props.onEnterTrimMode}
          video={video}
        />
        {hasEditorPro && (
          <FeatureFlaggedComponent
            flag={ROLLOUT_FILE_ATTACHMENTS}
            activationValue={true}
          >
            <AttachmentWidget />
          </FeatureFlaggedComponent>
        )}
      </div>
    );
  }

  connect(triggerHandler) {
    auth.openOAuthPopup(SLACK_APP, data => {
      let redirectURL;
      const cameFrom = getParam('redirect_after');

      this.trackEvent(SHARE_CTA_INSTALL_SLACK_CLICKED);
      triggerHandler();

      redirectURL = decodeURIComponent(cameFrom);

      if (data.error != null) {
        redirectURL += '?oauth_error=' + data.error;
      }

      if (data.loginSuccess) {
        extension.signalExtensionLogin(() => {
          window.location.href = redirectURL;
        });
      } else {
        window.location.href = redirectURL;
      }
    });
  }

  componentDidUpdate(prevProps) {
    const isEnter = prop => !prevProps[prop] && this.props[prop];
    const isExit = prop => prevProps[prop] && !this.props[prop];

    if (isEnter('editorActive')) {
      videoSession.endSession(true);
      this.exitAnimation = this.getWidgetAnimeInstance('.edit-video');
    }

    if (isEnter('attachmentWidgetActive')) {
      videoSession.endSession(true);
      this.exitAnimation = this.getWidgetAnimeInstance('.attachment-widget');
    }

    if (isEnter('speechFlow')) {
      videoSession.endSession(true);
      this.exitAnimation = this.getWidgetAnimeInstance(
        '[widget="speech-coach"]'
      );
    }

    if (isEnter('viewerInsightsActive')) {
      videoSession.endSession(true);
      this.exitAnimation = this.getWidgetAnimeInstance('.viewer-insights');
    }

    const reverseAnime = [
      'editorActive',
      'viewerInsightsActive',
      'attachmentWidgetActive',
      'speechFlow',
    ].some(el => isExit(el));

    if (reverseAnime && this.exitAnimation) {
      this.exitAnimation();
    }
  }

  getWidgetAnimeInstance(unaffectedClass) {
    const bodyClass = unaffectedClass.substr(1) + '-active';
    const els = document.querySelectorAll(
      `.right-panel>*:not(${unaffectedClass})`
    );

    requestAnimationFrame(() => {
      els.forEach(el => el.classList.add('right-panel-faded-elements'));
      document.body.classList.add(bodyClass, 'right-panel-box-raised');
    });

    return () => {
      requestAnimationFrame(() => {
        document.body.classList.add('right-panel-box-back');
        document.body.classList.remove('right-panel-box-raised');
      });
      setTimeout(() => {
        requestAnimationFrame(() => {
          els.forEach(el => el.classList.remove('right-panel-faded-elements'));
          document.body.classList.remove(bodyClass, 'right-panel-box-back');
        });
      }, 250);
    };
  }

  trackEvent(type) {
    analytics.track(type, { video_id: this.props.video.id });
  }
}

const mapStateToProps = state => {
  return {
    attachmentWidgetActive: attachmentsActive(state),
    hasPro: selectHasPro(state),
    video: selectVideo(state),
    editorActive: selectEditorIsActive(state),
    speechFlow: state.speechCoach.speechFlow,
    useEmojis: selectVideoUseEmojis(state),
    viewerInsightsActive: viewerInsightsActive(state),
  };
};

const mapDispatchToProps = dispatch => ({
  completeTriggerCreator: trigger => dispatch(completeTriggerCreator(trigger)),
  toggleViewerInsightsUI: () => dispatch(toggleViewerInsightsUI()),
});

export default connect(mapStateToProps, mapDispatchToProps)(RightPanel);
